import { Fragment, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronDown, CreditCard, ExternalLink, FlaskConical, Plus } from 'lucide-react';

import { BILLING_PATH, Callout, ErrorCallout, InlineConfirm, Lamp, PageHead, Segmented, SkeletonRows, Spinner } from '../components/ui';
import { api, ApiError, asApiError } from '../lib/api';
import { dayLabel, grantLabel, money, monthName, perSecond, shortDate } from '../lib/format';
import { useAction, useDocumentTitle, useResource } from '../lib/hooks';
import { useBilling, useSession } from '../lib/session';
import type { Billing, Invoice } from '../lib/types';

const INVOICE_STATUS: Record<Invoice['status'], { label: string; lamp: Parameters<typeof Lamp>[0]['tone']; note?: string }> = {
  paid: { label: 'Paid', lamp: 'accent' },
  open: { label: 'Open', lamp: 'amber' },
  failed: { label: 'Failed', lamp: 'refused' },
  deferred: { label: 'Deferred', lamp: 'hollow', note: 'Under Stripe’s $0.50 minimum, so it carries to next month' },
};

const BRANDS: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover', jcb: 'JCB', diners: 'Diners Club', unionpay: 'UnionPay' };
const brandName = (brand: string) => BRANDS[brand.toLowerCase()] ?? brand.charAt(0).toUpperCase() + brand.slice(1);

/** Back from checkout: ?checkout=success&session_id=… attaches the card. */
function useCheckoutReturn(onBilling: (b: Billing) => void) {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<{ kind: 'idle' } | { kind: 'busy' } | { kind: 'ok'; billing: Billing } | { kind: 'cancelled' } | { kind: 'error'; error: ApiError }>({ kind: 'idle' });
  const handled = useRef(false);
  useEffect(() => {
    const checkout = params.get('checkout');
    const sessionId = params.get('session_id');
    if (!checkout || handled.current) return;
    handled.current = true;
    const clear = () => {
      const next = new URLSearchParams(params);
      next.delete('checkout');
      next.delete('session_id');
      setParams(next, { replace: true });
    };
    if (checkout === 'cancelled' || !sessionId) {
      setState({ kind: 'cancelled' });
      clear();
      return;
    }
    setState({ kind: 'busy' });
    api<Billing>('/v1/billing/checkout/complete', { method: 'POST', body: { sessionId } })
      .then((billing) => {
        onBilling(billing);
        setState({ kind: 'ok', billing });
      })
      .catch((err) => setState({ kind: 'error', error: asApiError(err) }))
      .finally(clear);
  }, [params, setParams, onBilling]);
  return state;
}

function CreditTile({ billing }: { billing: Billing | undefined }) {
  const c = billing?.credit;
  const share = c && c.grantedUsd > 0 ? c.remainingUsd / c.grantedUsd : 0;
  /* Organizations get no sign-up credit: say so rather than "$0.00 of $0.00". */
  const none = c !== undefined && c.grantedUsd === 0;
  /* Name the grants once there is more than the sign-up credit: the YC deal, what it has left, and when it ends. */
  const live = c?.grants.filter((g) => g.active && g.remainingUsd > 0) ?? [];
  const named = live.some((g) => g.reason !== 'free');
  return (
    <section className="surface tile" aria-labelledby="credit-title">
      <h2 className="tile__label" id="credit-title">
        Credit
      </h2>
      <div className="tile__value">{c ? money(c.remainingUsd) : <span className="skeleton" style={{ width: 110, height: 30 }} />}</div>
      <div className="tile__sub">{c ? (none ? 'Organizations pay from the first second' : `of ${money(c.grantedUsd)} granted`) : ' '}</div>
      <div className="bar" role="meter" aria-label="Credit left" aria-valuemin={0} aria-valuemax={c?.grantedUsd ?? 0} aria-valuenow={c?.remainingUsd ?? 0}>
        <span style={{ width: `${Math.min(100, share * 100)}%` }} />
      </div>
      {named ? (
        <ul className="tile__grants">
          {live.map((g) => (
            <li key={g.id}>
              <span>{grantLabel(g.reason)}</span>
              <span>
                {money(g.remainingUsd)} left{g.expiresAt !== null ? `, until ${shortDate(g.expiresAt)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** The card on file drawn as a card, or an empty one to add it. */
function PaymentTile({ billing, isOwner }: { billing: Billing | undefined; isOwner: boolean }) {
  const { setBilling } = useBilling();
  const checkout = useAction(async () => {
    const { url } = await api<{ url: string }>('/v1/billing/checkout', { method: 'POST', body: { returnUrl: `${window.location.origin}${BILLING_PATH}` } });
    window.location.assign(url);
    return url;
  });
  const removeCard = useAction(async () => api<Billing>('/v1/billing/card', { method: 'DELETE' }));
  const card = billing?.card;
  const failed = billing?.status === 'past_due';

  return (
    <section className="surface tile" aria-labelledby="card-title">
      <h2 className="tile__label" id="card-title">
        Payment method
      </h2>
      <div className="pay">
        {!billing ? (
          <span className="ccard ccard--empty" aria-hidden="true" />
        ) : card ? (
          <div className="ccard" role="img" aria-label={`${brandName(card.brand)} ending in ${card.last4}`}>
            <span className="ccard__chip" aria-hidden="true" />
            <span className="ccard__brand">{brandName(card.brand)}</span>
            <span className="ccard__num">•••• {card.last4}</span>
            <span className="ccard__exp">
              {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
            </span>
          </div>
        ) : (
          <div className="ccard ccard--empty">
            {isOwner && billing.mode !== 'off' ? (
              <button type="button" className="btn btn--sm btn--primary" onClick={() => void checkout.run()} disabled={checkout.busy} aria-busy={checkout.busy}>
                {checkout.busy ? <Spinner size={13} /> : <Plus aria-hidden="true" />}
                Add card
              </button>
            ) : (
              <span className="ccard__none">
                <CreditCard aria-hidden="true" />
                {billing.mode === 'off' ? 'Cards open soon' : 'No card'}
              </span>
            )}
          </div>
        )}
        {billing ? (
          <div className="pay__side">
            <p className={`pay__note${failed ? ' is-bad' : ''}`}>
              {failed ? 'The last charge failed' : card ? 'Billed on the 1st of each month' : billing.credit.remainingUsd > 0 ? 'Needed once the free credit runs out' : 'Needed before work can run'}
            </p>
            {isOwner && card ? (
              <div className="row" style={{ gap: 6 }}>
                <button type="button" className="btn btn--sm" onClick={() => void checkout.run()} disabled={checkout.busy} aria-busy={checkout.busy}>
                  {checkout.busy ? <Spinner size={13} /> : null}
                  Replace card
                </button>
                <InlineConfirm
                  trigger="Remove card"
                  question="Remove this card?"
                  confirmLabel="Remove"
                  busy={removeCard.busy}
                  onConfirm={async () => {
                    const updated = await removeCard.run();
                    if (updated) setBilling(updated);
                  }}
                />
              </div>
            ) : null}
            {!isOwner ? <p className="pay__note">Only an owner can change the card.</p> : null}
          </div>
        ) : null}
      </div>
      {checkout.error || removeCard.error ? <ErrorCallout error={(checkout.error ?? removeCard.error)!} /> : null}
    </section>
  );
}

/** The monthly budget: an amount, enforced or not, and where this month stands against it. */
function SpendingLimit({ billing, isOwner }: { billing: Billing | undefined; isOwner: boolean }) {
  const { setBilling } = useBilling();
  const [amount, setAmount] = useState('');
  const [enforce, setEnforce] = useState(false);
  const [saved, setSaved] = useState(false);
  const save = useAction(async (body: { limitUsd: number | null; enforce: boolean }) => api<Billing>('/v1/billing/budget', { method: 'PUT', body }));

  const b = billing?.budget;
  useEffect(() => {
    if (!b) return;
    setAmount(b.limitUsd === null ? '' : String(b.limitUsd));
    setEnforce(b.limitUsd !== null && b.enforce);
    // Only when the saved limit changes: background refreshes don't clobber edits.
  }, [b?.limitUsd, b?.enforce]);

  const blank = amount.trim() === '';
  const parsed = Number(amount.replace(/[$,\s]/g, ''));
  const amountOk = blank || (Number.isFinite(parsed) && parsed > 0);
  const nextLimit = blank || !amountOk ? null : Math.round(parsed * 100) / 100;
  const problem = !amountOk ? 'Enter an amount in dollars, like 50.' : blank && enforce ? 'Enter an amount to enforce.' : null;
  const dirty = b !== undefined && (nextLimit !== b.limitUsd || (nextLimit !== null && enforce !== b.enforce));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (problem) return;
    setSaved(false);
    const result = await save.run({ limitUsd: nextLimit, enforce: nextLimit !== null && enforce });
    if (result) {
      setBilling(result);
      setSaved(true);
    }
  }

  const limit = b?.limitUsd ?? null;
  /* Only what the team pays counts against the limit: spend beyond the credit, and work in flight the credit left won't cover. */
  const spent = b?.spentUsd ?? 0;
  const reserved = Math.max(0, (billing?.period.reservedUsd ?? 0) - (billing?.credit.remainingUsd ?? 0));
  const scale = limit !== null ? Math.max(limit, spent + reserved) || 1 : 1;
  const tone = limit !== null && spent >= limit ? ' is-over' : limit !== null && spent >= limit * 0.8 ? ' is-warn' : '';

  return (
    <section className="surface limit" id="limit" aria-labelledby="limit-title">
      <div className="surface__head">
        <h2 className="surface__title" id="limit-title">
          Spending limit
        </h2>
        {billing ? <span className="muted small">{monthName(billing.period.id)}</span> : null}
      </div>
      <div className="surface__body stack">
        <form className="limit__form" onSubmit={submit}>
          <fieldset className="limit__fields" disabled={!isOwner}>
            <legend className="visually-hidden">Monthly spending limit</legend>
            <div className="field">
              <label htmlFor="limit-amount">Monthly limit</label>
              <div className="money-input">
                <span aria-hidden="true">$</span>
                <input
                  id="limit-amount"
                  className="input"
                  inputMode="decimal"
                  placeholder="No limit"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setSaved(false);
                  }}
                  aria-invalid={problem && !amountOk ? true : undefined}
                  aria-describedby="limit-hint"
                />
              </div>
            </div>
            <Segmented
              label="Enforcement"
              options={[
                { value: 'on', label: 'Enforced' },
                { value: 'off', label: 'Not enforced' },
              ]}
              value={enforce ? 'on' : 'off'}
              onChange={(v) => {
                setEnforce(v === 'on');
                setSaved(false);
              }}
            />
          </fieldset>
          {isOwner ? (
            <div className="row" style={{ gap: 10 }}>
              <button type="submit" className="btn btn--primary" disabled={!dirty || problem !== null || save.busy} aria-busy={save.busy}>
                {save.busy ? <Spinner /> : null}
                Save
              </button>
              {saved && !dirty ? <span className="small muted">Saved</span> : null}
            </div>
          ) : null}
        </form>

        {billing ? (
          <div>
            {limit !== null ? (
              <div
                className="bar bar--spend"
                role="meter"
                aria-label="Spend this month against the limit"
                aria-valuemin={0}
                aria-valuemax={limit}
                aria-valuenow={Math.round(spent * 100) / 100}
                aria-valuetext={`${money(spent)} spent and ${money(reserved)} reserved of ${money(limit)}`}
              >
                <span className={tone} style={{ width: `${Math.min(100, (spent / scale) * 100)}%` }} />
                {reserved > 0 ? <i style={{ width: `${Math.max(0.6, Math.min(100, (reserved / scale) * 100))}%` }} /> : null}
              </div>
            ) : null}
            <div className="limit__legend">
              <span>
                <b>{money(spent)}</b> spent{billing.credit.usedUsd > 0 ? ' beyond credit' : ''}
              </span>
              <span>
                <b>{money(reserved)}</b> reserved by work in flight
              </span>
              <span className="limit__total">{limit !== null ? <><b>{money(limit)}</b> limit</> : 'No limit'}</span>
            </div>
          </div>
        ) : (
          <span className="skeleton" style={{ height: 6 }} />
        )}

        <p className="hint" id="limit-hint" style={{ margin: 0 }}>
          {problem ? (
            <span className="field-error">{problem}</span>
          ) : (
            <>
              {!isOwner ? 'Only an owner can change the limit.' : enforce ? 'New work that would go over the limit is refused.' : 'Work keeps running past the limit; it only marks the line.'}{' '}
              Credit never counts against it.{' '}
              <Link to="/docs#spending-limits">How spending limits work</Link>
            </>
          )}
        </p>
        {save.error ? <ErrorCallout error={save.error} /> : null}
      </div>
    </section>
  );
}

export function BillingPage() {
  useDocumentTitle('Billing');
  const { billing, setBilling, reloadBilling } = useBilling();
  const { me } = useSession();
  // The card and the limit are the owners' (403 forbidden for members).
  const isOwner = me?.role === 'owner';
  const invoices = useResource<{ invoices: Invoice[] }>('/v1/billing/invoices');
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);
  const ret = useCheckoutReturn(setBilling);
  const { hash } = useLocation();

  useEffect(() => {
    void reloadBilling();
  }, [reloadBilling]);

  // /budget lands on #limit: scroll there once the page has its height.
  const loaded = billing !== undefined;
  useEffect(() => {
    if (loaded && hash === '#limit') document.getElementById('limit')?.scrollIntoView({ block: 'start' });
  }, [loaded, hash]);

  const b = billing;
  const nextInvoice = b ? dayLabel(new Date(b.period.end).toISOString().slice(0, 10)) : '';
  const rows = invoices.data?.invoices ?? [];

  return (
    <>
      {b?.mode === 'test' ? (
        <div className="banner" role="note">
          <FlaskConical width={15} height={15} aria-hidden="true" />
          <strong>Test mode</strong>
          <span>
            {b.portal
              ? 'Stripe test mode: no real money moves. Pay with 4242 4242 4242 4242; 4000 0000 0000 0341 attaches, then declines.'
              : 'Cards come from the built-in test checkout and no real money moves.'}
          </span>
        </div>
      ) : b?.mode === 'off' ? (
        <div className="banner" role="note">
          <CreditCard width={15} height={15} aria-hidden="true" />
          <strong>Card payments open soon</strong>
          <span>Until then every account runs on its free credit, and nothing is charged.</span>
        </div>
      ) : null}
      <PageHead
        title="Billing"
        lede={
          b ? (
            <>
              <span className="nowrap">Pay as you go ·</span> <span className="nowrap">{money(b.plan.usdPerHour)} an hour,</span>{' '}
              <span className="nowrap">{perSecond(b.plan.usdPerHour / 3600)} a second ·</span> <span className="nowrap">up to {b.plan.concurrentSandboxes} open sandboxes</span>
            </>
          ) : (
            ' '
          )
        }
      />

      <div className="sheet">
        {ret.kind === 'busy' ? (
          <Callout tone="info" role="status" title="Attaching your card…" />
        ) : ret.kind === 'ok' ? (
          <Callout tone="ok" role="status" title={ret.billing.card ? `Card added: ${brandName(ret.billing.card.brand)} •••• ${ret.billing.card.last4}.` : 'Checkout complete.'}>
            {ret.billing.status === 'past_due' ? 'But the latest charge on it failed.' : null}
          </Callout>
        ) : ret.kind === 'cancelled' ? (
          <Callout tone="info" role="status" title="Checkout cancelled. No card was added." />
        ) : ret.kind === 'error' ? (
          <ErrorCallout error={ret.error} title="The card couldn’t be attached" />
        ) : null}

        {b?.status === 'past_due' ? (
          <Callout tone="error" role="alert" title="The last charge failed, so new work is paused">
            {b.failureMessage ? `${b.failureMessage} ` : ''}Running work continues.{' '}
            {isOwner ? 'Replace the card and the invoice is charged again at once.' : 'Ask a team owner to replace the card.'}
          </Callout>
        ) : null}

        <div className="tiles">
          <CreditTile billing={b} />
          <PaymentTile billing={b} isOwner={isOwner} />
        </div>

        <SpendingLimit billing={b} isOwner={isOwner} />

        <section className="surface" aria-labelledby="inv-title">
          <div className="surface__head">
            <h2 className="surface__title" id="inv-title">
              Invoices
            </h2>
            {b ? (
              <span className="muted small">
                Next on {nextInvoice}, about {money(b.period.estimatedInvoiceUsd)}
              </span>
            ) : null}
          </div>
          {invoices.error ? (
            <div className="surface__body">
              <ErrorCallout error={invoices.error} onRetry={() => void invoices.reload()} />
            </div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Invoice</th>
                    <th scope="col">Period</th>
                    <th scope="col" className="r">
                      Amount
                    </th>
                    <th scope="col">Status</th>
                    <th scope="col" className="r">
                      <span className="visually-hidden">Details</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.loading && !invoices.data ? (
                    <SkeletonRows cols={5} rows={2} />
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="m-full table-empty">
                        No invoices yet. Each month’s is made on the 1st of the next.
                      </td>
                    </tr>
                  ) : (
                    rows.map((inv) => {
                      const st = INVOICE_STATUS[inv.status];
                      const isOpen = openInvoice === inv.id;
                      return (
                        <Fragment key={inv.id}>
                          <tr className="is-link m-stack" onClick={() => setOpenInvoice(isOpen ? null : inv.id)}>
                            <td className="m-title mono small nowrap">{inv.number}</td>
                            <td className="m-meta m-inline">{monthName(inv.period)}</td>
                            <td className="r num m-end" style={{ fontWeight: 600 }}>
                              {money(inv.totalUsd)}
                            </td>
                            <td className="m-meta m-inline" title={inv.failureMessage ?? st.note}>
                              <span className={`pill${inv.status === 'failed' ? ' pill--failed' : inv.status === 'deferred' ? ' pill--muted' : ''}`}>
                                <Lamp tone={st.lamp} />
                                {st.label}
                              </span>
                            </td>
                            <td className="r m-meta m-inline">
                              {inv.hostedUrl ? (
                                <a className="btn btn--sm btn--quiet" href={inv.hostedUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                  View
                                  <ExternalLink aria-hidden="true" />
                                </a>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn--sm btn--quiet"
                                  aria-expanded={isOpen}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenInvoice(isOpen ? null : inv.id);
                                  }}
                                >
                                  Lines
                                  <ChevronDown aria-hidden="true" style={{ transform: isOpen ? 'rotate(180deg)' : undefined }} />
                                </button>
                              )}
                            </td>
                          </tr>
                          {isOpen ? (
                            <tr>
                              <td colSpan={5} className="m-full">
                                <ul className="invoice-lines" style={{ maxWidth: 560 }}>
                                  {inv.lines.map((l, i) => (
                                    <li key={i}>
                                      <span>{l.description}</span>
                                      <span>{l.amountUsd < 0 ? `−${money(-l.amountUsd)}` : money(l.amountUsd)}</span>
                                    </li>
                                  ))}
                                  {inv.carriedUsd > 0 && !inv.lines.some((l) => /carried/i.test(l.description)) ? (
                                    <li>
                                      <span>Carried from last month</span>
                                      <span>{money(inv.carriedUsd)}</span>
                                    </li>
                                  ) : null}
                                  <li style={{ color: 'var(--ink)', fontWeight: 600, borderTop: '1px solid var(--line)', paddingTop: 6, marginTop: 4 }}>
                                    <span>Total{st.note ? `: ${st.note.toLowerCase()}` : ''}</span>
                                    <span>{money(inv.totalUsd)}</span>
                                  </li>
                                  {inv.failureMessage ? (
                                    <li style={{ color: 'var(--refused)' }}>
                                      <span>{inv.failureMessage}</span>
                                      <span />
                                    </li>
                                  ) : null}
                                </ul>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
